/**
 * What an event actually set off: every workflow it triggered, and how far
 * each one has got — including the steps still ahead.
 *
 * The steps-still-ahead part is the whole point. A run pins the definition it
 * started with, so the view can answer "what happens next" rather than only
 * "what happened". For a unit alarm that is the difference between reading a
 * log and knowing a mechanic is about to be dispatched.
 */
import {
  Badge,
  Skeleton,
  Timeline,
  type BadgeVariant,
} from '@servicecompressionllc/sc-design-system';
import type { EventRun, EventRunStep } from '../../lib/types.js';
import { useEventRunsLogic } from './useEventsLogic.js';

/** Engine words never reach the screen. */
import { Link } from 'react-router-dom';

const RUN_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  'waiting to run': { label: 'Waiting to run', variant: 'neutral' },
  running: { label: 'Running', variant: 'info' },
  suspended: { label: 'Waiting on someone', variant: 'warning' },
  deferred: { label: 'Paused — daily limit', variant: 'info' },
  done: { label: 'Finished', variant: 'success' },
  failed: { label: 'Something went wrong', variant: 'danger' },
};
const runStatus = (s: string) => RUN_STATUS[s] ?? { label: s, variant: 'neutral' as BadgeVariant };

/** Step type → the verb an admin would use for it. */
const STEP_VERB: Record<string, string> = {
  filter: 'only if',
  branch: 'split',
  notify: 'notify',
  webhook: 'send',
  emit: 'start',
  llm: 'ask the ai',
  tool: 'action',
  approval: 'ask a person',
};

/** What the step is/was doing, in the same language the builder uses. */
const stepTitle = (step: EventRunStep): string => {
  if (step.name) return step.name;
  switch (step.type) {
    case 'filter':
      return 'Check the conditions';
    case 'branch':
      return 'Route on the answer';
    case 'notify':
      return 'Send a message';
    case 'webhook':
      return 'Send to connected systems';
    case 'emit':
      return 'Start another workflow';
    case 'llm':
      return 'Ask the AI';
    case 'agent':
      return 'AI agent investigation';
    case 'tool':
      return 'Run an action';
    case 'approval':
      return 'Ask a person to approve';
    case 'report':
      return 'Run a report';
    case 'bot':
      return 'Tell a bot';
    default:
      return step.type;
  }
};

const clock = (iso: string | null): string | undefined =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : undefined;

/** The one way a run's steps render — shared with the workflow editor's
 * Run history page so both surfaces tell the story identically. */
export const RunStepsTimeline = ({
  steps,
  ariaLabel,
}: {
  steps: EventRunStep[];
  ariaLabel: string;
}) => (
  <Timeline aria-label={ariaLabel}>
    {steps.map((step) => (
      <Timeline.Item
        key={step.path}
        status={step.status === 'previewed' ? 'done' : step.status}
        pulse={step.status === 'active'}
        label={STEP_VERB[step.type] ?? step.type}
        title={stepTitle(step)}
        meta={step.status === 'active' ? 'now' : clock(step.at)}
      >
        {step.detail}
      </Timeline.Item>
    ))}
  </Timeline>
);

const RunCard = ({ run }: { run: EventRun }) => {
  const status = runStatus(run.status);
  const doneCount = run.steps.filter((s) => s.status !== 'pending').length;
  return (
    <div className="rounded-lg border border-outline bg-surface-elevated/70">
      <div className="flex flex-wrap items-baseline gap-2 border-b border-outline px-3 py-2">
        {run.kind === 'composed' ? (
          // Built here → the builder edits it; the name is the link that
          // closes the events → workflows loop. Built-in flows have no page.
          <Link
            to={`/settings/workflows/${run.workflowId}`}
            className="text-sm font-semibold text-ink hover:text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {run.workflowName}
          </Link>
        ) : (
          <span className="text-sm font-semibold text-ink">{run.workflowName}</span>
        )}
        <span className="text-[0.68rem] uppercase tracking-wider text-ink-subtle">
          {run.kind === 'system' ? 'built in' : 'built here'}
        </span>
        <Badge variant={status.variant}>{status.label}</Badge>
        <span className="ml-auto text-xs tabular-nums text-ink-subtle">
          {run.startedAt === null
            ? `${run.steps.length} steps ahead`
            : `step ${Math.min(doneCount, run.steps.length)} of ${run.steps.length}`}
        </span>
      </div>
      <div className="px-3 py-2">
        <RunStepsTimeline steps={run.steps} ariaLabel={`${run.workflowName} steps`} />
      </div>
    </div>
  );
};

export const EventRuns = ({
  tenantId,
  eventId,
}: {
  tenantId: string | undefined;
  eventId: string;
}) => {
  const { isLoading, data, error } = useEventRunsLogic(tenantId, eventId);

  if (isLoading) return <Skeleton className="h-32 w-full rounded-lg" />;
  if (error) {
    return (
      <p className="text-sm text-ink-muted">Couldn’t load what this set off ({error.message}).</p>
    );
  }
  if (data?.unrouted) {
    return (
      <p className="text-sm text-ink-muted">
        Nothing is set up to handle this kind of event, so it was recorded and closed. Build a
        workflow for it if something should happen.
      </p>
    );
  }
  if (!data || data.runs.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        This was handled by a built-in workflow, which doesn’t report its steps yet.
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      {data.runs.map((run) => (
        <RunCard key={run.id} run={run} />
      ))}
      {data.runs.length > 1 && (
        <p className="text-xs text-ink-subtle">
          {data.runs.length} workflows are listening to this kind of event.
        </p>
      )}
    </div>
  );
};
